import _             from 'underscore';
import React, { FC } from 'react';

import { QueryOptions, useQuery }                                              from '@apollo/react-hooks';
import { SystemAccountingQueries }                                             from '../../../../queries';
import { formatOption }                                                        from '../../../../util';
import { EMPTY_VALUE_DISPLAY, USERNAMES }                                      from '../../../../constants/FormatStringEnums';
import { SelectField, SelectFieldProps }                                       from '../../../components/forms';
import { UserService }                                                         from '../../../../services';
import { CorvalAccountLike, CorvalOrganization, CorvalProject, GQLNode, Uuid } from '../../../../types';


interface QueryDropdownProps {
    id: Uuid,
    query: GQLNode,

    queryResults( data: any ): CorvalAccountLike[],

    queryOptions?: Omit<QueryOptions, 'query'>,
    loading?: boolean,
    nameFormat?: number,
    noIDLabel?: string,
}

interface UserDropdownProps extends Pick<QueryDropdownProps, 'nameFormat' | 'loading'> {
    users: CorvalAccountLike[]
}

const defaultProps: QueryDropdownProps = {
    id:           null,
    loading:      false,
    query:        null,
    queryOptions: null,
    queryResults: data => data,
    noIDLabel:    EMPTY_VALUE_DISPLAY,
    nameFormat:   USERNAMES.FULL_LASTNAME_FIRST,
}

const UsersDropdown: FC<UserDropdownProps> = ( _props ) => {

    const { users, nameFormat, ...props } = _props;

    let options = ( _.isArray( users ) && users.length )
                  ? _.chain( users )
                     .sortBy( ( nameFormat === USERNAMES.INITIALS ) ? 'firstName' : 'lastName' )
                     .map( user => formatOption( user.id, UserService.getFormattedUserName( user, nameFormat ) ) )
                     .value()
                  : [];

    return (
        <SelectField{ ...props } options={ options }/>
    );
};

const QueryDropdown: FC<QueryDropdownProps> = ( _props ) => {

    const { noIDLabel, nameFormat, query, queryOptions, queryResults, ...props } = _props;

    // no id, no bueno.
    if( !props.id ){
        return <SelectField{ ...props } options={ [] } placeholder={ noIDLabel }/>;
    }

    let { data, loading, error } = useQuery( query, queryOptions ),
        isLoading                = ( props.loading || loading );

    let items = ( isLoading || !data ) ? [] : queryResults( data );

    return (
        <UsersDropdown
            { ...props }
            loading={ isLoading }
            users={ items }
            nameFormat={ nameFormat }>
        </UsersDropdown>
    );
};

const ProjectMembersDropdown: FC<{ id: Uuid }> = ( props ) => {

    return (
        <QueryDropdown
            { ...props }
            query={ SystemAccountingQueries.queries.GET_PROJECT( `projectMembers{ account{ id lastName firstName } } ` ) }
            queryOptions={ {
                variables:   { id: props.id },
                fetchPolicy: 'cache-and-network'
            } }
            queryResults={ ( data: { projects: CorvalProject[] } ) => {
                return _.pluck( data.projects[ 0 ].projectMembers, 'account' )
            } }
        />
    );
};

const OrganizationalMembersDropdown: FC<Omit<QueryDropdownProps, 'query' | 'queryOptions' | 'queryResults'> & Omit<SelectFieldProps, 'options'> & { organizationId: Uuid }> = ( props ) => {

    return (
        <QueryDropdown
            { ...props }
            query={ SystemAccountingQueries.queries.GET_ORGANIZATION( `organizationMembers{ account{ id lastName firstName } } ` ) }
            queryOptions={ {
                variables:   { id: props.organizationId },
                fetchPolicy: 'cache-and-network'
            } }
            queryResults={ ( data: { organizations: CorvalOrganization[] } ) => {
                return _.pluck( data.organizations[ 0 ].organizationMembers, 'account' )
            } }
        />
    );
};

QueryDropdown.defaultProps = defaultProps;

export { UsersDropdown, ProjectMembersDropdown, OrganizationalMembersDropdown };
